import { Component, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Prize,
  Sponsor,
  Criteria,
  Giveaway,
  Draw,
} from '@app/_models/giveaway';
import { MatDialog } from '@angular/material/dialog';
import { GiveawayService } from '@app/services/giveaways/giveaways.service';
import { Validators, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { GiveawayNewPrizeComponent } from '../giveaway-new-prize/giveaway-new-prize.component';
import { GiveawayDeliverPrizeComponent } from '../giveaway-deliver-prize/giveaway-deliver-prize.component';
import { Dictionary } from '@app/_models/shared.model';
import { Location } from '@angular/common';
import { PlayersService } from '@app/services/players.service';
import { AddSponsorComponent } from '@app/components/shared/add-sponsor/add-sponsor.component';
import { catchError, retry } from 'rxjs/operators';

@Component({
  selector: 'giveaway-detail',
  templateUrl: './giveaway-detail.component.html',
  styleUrls: ['./giveaway-detail.component.scss'],
})
export class GiveawayDetailComponent implements OnInit {
  constructor(
    private activatedRoute: ActivatedRoute,
    private builder: FormBuilder,
    private giveawayService: GiveawayService,
    private _playerService: PlayersService,
    public dialog: MatDialog,
    private router: Router,
    private _snackBar: MatSnackBar,
    private _location: Location
  ) {}

  sponsors: Sponsor[] = [];
  criterias: Criteria[] = [];
  status: Dictionary = {
    name: '',
    value: 0,
  };
  giveawayForm = this.builder.group({
    name: ['', Validators.required],
    criteria: ['', Validators.required],
    start_date: ['', Validators.required],
    end_date: ['', Validators.required],
    claim_time: [
      7,
      [Validators.required, Validators.min(7), Validators.max(60)],
    ],
    draw_datetime: [{ value: '', disabled: true }, Validators.required],
  });
  drawDatetime: string;
  currentId: number;
  draws: Draw[] = [];
  claimTime: moment.Moment = moment();

  ngOnInit(): void {
    this.currentId = this.activatedRoute.snapshot.params.id;
    if (this.currentId) {
      this.loadCurrentGiveaway();
    }
    this.giveawayService
      .getCriterias()
      .subscribe((criterias: Array<Criteria>) => {
        this.criterias = criterias;
      });
  }

  checkExecuteStatus(): boolean {
    const isAfterDrawTime = moment(
      this.drawDatetime,
      'DD/MM/YYYY HH:mm'
    ).isBefore(moment());
    if (this.status.value === 1 && isAfterDrawTime) {
      return true;
    }
    return false;
  }

  checkState(giveawayStatusValue): void {
    this.giveawayService.getStates().subscribe((states: Dictionary[]) => {
      const actualState = states.find(e => {
        return e.value == giveawayStatusValue;
      });
      this.status = actualState;
      if (this.status.value !== 0) {
        this.giveawayForm.disable();
      }
    });
  }

  loadCurrentGiveaway(): void {
    this.giveawayService
      .getGiveaway(this.currentId)
      .subscribe((giveaway: Giveaway) => {
        this.giveawayForm.markAllAsTouched();
        this.giveawayForm.setValue({
          name: giveaway.name,
          criteria: giveaway.criteria,
          start_date: moment(giveaway.start_date, moment.ISO_8601),
          end_date: moment(giveaway.end_date, moment.ISO_8601),
          draw_datetime: moment(giveaway.draw_datetime, moment.ISO_8601),
          claim_time:
            moment(giveaway.claim_time, moment.ISO_8601).diff(
              moment(giveaway.draw_datetime, moment.ISO_8601),
              'days'
            ) + 1,
        });
        this.getGiveawayDraws(giveaway.id);
        this.getGiveawaySponsors(giveaway.sponsors);
        this.checkState(giveaway.status);
        this.checkDrawDatetime();
        // Globalizo la fecha de vigencia para calcular el tiempo restanto por draw
        this.claimTime = moment(giveaway.claim_time, moment.ISO_8601);
      });
  }

  checkDrawDatetime(): void {
    const endDate = this.giveawayForm.get('end_date').value;
    if (endDate) {
      const drawTime = moment(endDate)
        .set('hour', 18)
        .add(1, 'days');
      this.giveawayForm.get('draw_datetime').setValue(drawTime);
      this.drawDatetime = moment(drawTime)
        .set('hour', 18)
        .format('DD/MM/YYYY HH:mm');
    }
  }

  getGiveawaySponsors(sponsorsIds: number[]): void {
    sponsorsIds.forEach((e: number) => {
      this.giveawayService.getSponsor(e).subscribe((sponsor: Sponsor) => {
        this.sponsors.push(sponsor);
      });
    });
  }

  getGiveawayDraws(giveawayId: number): void {
    this.giveawayService
      .getDrawsByGiveaway(giveawayId)
      .subscribe((draws: Draw[]) => {
        this.draws = draws;
        draws.forEach((draw: Draw) => {
          this.giveawayService
            .getPrizeByDraw(draw.prize)
            .subscribe((prize: Prize) => {
              draw.prizeDetail = prize;
            });
          if (draw.winner) {
            this._playerService.getPlayer(draw.winner).subscribe(player => {
              draw.playerDetail = player;
            });
          }
        });
      });
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.draws, event.previousIndex, event.currentIndex);
  }

  chooseGiveawaySponsor(): void {
    const dialogRef = this.dialog.open(AddSponsorComponent, {
      width: '800px',
    });
    const sub = dialogRef.componentInstance.onAddedSponsors.subscribe(
      (data: Sponsor[]) => {
        this.sponsors = this.mergeWithoutDuplicated(this.sponsors, data);
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  deletePrize(index: number): void {
    this.draws.splice(index, 1);
  }

  addNewPrize(): void {
    const dialogRef = this.dialog.open(GiveawayNewPrizeComponent, {
      width: '800px',
    });
    const sub = dialogRef.componentInstance.onAddedDraws.subscribe(
      (data: Draw[]) => {
        this.draws = [...this.draws, ...data];
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  removeSponsor(idx): void {
    this.sponsors.splice(idx, 1);
  }

  createDraws(giveaway: Giveaway): void {
    this.draws.forEach((draw: Draw, idx) => {
      draw = {
        ...draw,
        lottery: giveaway.id,
        order: idx + 1,
      };
      // Si el draw tiene id hay que hacer un update
      if (draw.id) {
        this.giveawayService.updateDraw(draw, giveaway.id, draw.id).subscribe();
      } else {
        this.giveawayService.createDraw(draw, giveaway.id).subscribe();
      }
    });
  }

  executeGiveaway() {
    this.giveawayService.executeGiveaway(this.currentId).subscribe();
  }

  actionFinished(message: string) {
    this.router.navigateByUrl('/giveaways');
    this._snackBar.open(message, '', {
      duration: 3000,
    });
  }

  onNewGiveawayCreated(publish = false): void {
    const newGiveaway: Giveaway = {
      name: this.giveawayForm.value.name,
      sponsors: this.reduceToIds(this.sponsors),
      criteria: this.giveawayForm.value.criteria,
      start_date: this.giveawayForm.value.start_date
        .toISOString()
        .split('T')[0],
      end_date: this.giveawayForm.value.end_date.toISOString().split('T')[0],
      claim_time: moment(this.giveawayForm.getRawValue().draw_datetime)
        .add('days', this.giveawayForm.value.claim_time)
        .toISOString()
        .split('T')[0],
    };
    if (!this.currentId) {
      this.giveawayService
        .createGiveaway(newGiveaway)
        .subscribe((newGiveaway: Giveaway) => {
          this.createDraws(newGiveaway);
          if (publish) {
            this.giveawayService.publishGiveaway(newGiveaway.id).subscribe(
              () => this.actionFinished('Sorteo creado correctamente'),
              err => {
                this._snackBar.open('Se produjo un error', '', {
                  duration: 3000,
                });
              }
            );
          } else {
            this.actionFinished('Sorteo creado correctamente');
          }
        });
    } else {
      this.giveawayService
        .updateGiveaway(this.currentId, newGiveaway)
        .subscribe(newGiveaway => {
          this.createDraws(newGiveaway);
          if (publish) {
            this.giveawayService.publishGiveaway(newGiveaway.id).subscribe(
              () => this.actionFinished('Sorteo actualizado correctamente'),
              err => {
                this._snackBar.open('Se produjo un error', '', {
                  duration: 3000,
                });
              }
            );
          } else {
            this.actionFinished('Sorteo actualizado correctamente');
          }
        });
    }
  }

  // Utiltities
  mergeWithoutDuplicated(...arrays): Sponsor[] {
    let jointArray = [];
    arrays.forEach((entireArray: Array<Sponsor>) => {
      jointArray = [...jointArray, ...entireArray];
    });
    const mergedArray = jointArray.filter((sponsor: Sponsor, idx) => {
      const foundIdx = jointArray.findIndex((e: Sponsor) => {
        return e.id === sponsor.id;
      });
      return idx === foundIdx;
    });
    return mergedArray;
  }

  reduceToIds(sponsors: Array<Sponsor>): number[] {
    const sponsorsIds: Array<number> = [];
    sponsors.forEach(e => {
      sponsorsIds.push(e.id);
    });
    return sponsorsIds;
  }

  openDeliverPrizeDialog(draw: Draw): void {
    const dialogRef = this.dialog.open(GiveawayDeliverPrizeComponent, {
      width: '800px',
      data: draw.id,
    });
    const sub = dialogRef.componentInstance.onCodeSubmited.subscribe(
      ([code, drawId]) => {
        this.giveawayService
          .deliverPrize(this.currentId, drawId, code)
          .subscribe(
            () => {
              this._snackBar.open('Premio entregado correctamente', '', {
                duration: 3000,
              });
              dialogRef.close();
              this.loadCurrentGiveaway();
            },
            (error: string) => {
              this._snackBar.open(error, '', {
                duration: 3000,
              });
            }
          );
      }
    );
    const closed = dialogRef.componentInstance.close.subscribe(() => {
      dialogRef.close();
    });
    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
      closed.unsubscribe();
    });
  }

  cancelGiveaway(): void {
    this.giveawayService.cancelGiveaway(this.currentId).subscribe(() => {
      this._snackBar.open('Sorteo cancelado correctamente', '', {
        duration: 3000,
      });
      this._location.back();
    });
  }

  checkDrawTimeLeft(): string {
    const days = moment(this.claimTime).diff(moment(), 'days');
    const hours = moment(this.claimTime).diff(moment(), 'hours');
    const minutes = moment(this.claimTime).diff(moment(), 'minutes');
    if (days > 1) {
      return `Quedan ${days} dias`;
    } else if (days < 1 && hours > 0) {
      return `Quedan ${hours} horas`;
    } else if (minutes > 0) {
      return `Quedan ${minutes} minutos`;
    } else {
      return 'Tiempo agotado';
    }
  }

  checkIfTimeExpired(): boolean {
    const minutes = moment(this.claimTime).diff(moment(), 'minutes');
    if (minutes < 0) {
      return true;
    }
    return false;
  }

  returnToList(): void {
    this._location.back();
  }
}
